import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { iconRegistry, iconColors, sizeVariants, iconUtils, accessibilityConfig } from './iconRegistry';
import iconPreloader from './iconPreloader';
import iconPerformanceMonitor from './iconPerformanceMonitor';
import { useIsMobile } from '../../utils/mobileDetection';
import './Icon.css';

/**
 * Centralized Icon component that handles both SVG and PNG assets with responsive sizing and accessibility
 * @param {string} name - Icon identifier from the registry
 * @param {string} size - Size variant: xs, sm, md, lg, xl
 * @param {string} color - Color theme for SVG icons
 * @param {string} variant - Icon variant (for future extensibility)
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alternative text (overrides default)
 * @param {boolean} animate - Enable animation for supported icons
 * @param {boolean} responsive - Enable responsive sizing (default: true)
 * @param {object} style - Additional inline styles
 * @param {string} background - Background color for contrast validation
 */
const Icon = ({
  name,
  size = 'md',
  color = 'primary',
  variant = 'default',
  className = '',
  alt,
  animate = false,
  responsive = true,
  lazy = false,
  style = {},
  background = '#FFFFFF',
  ...props
}) => {
  // Mobile detection for conditional animations
  const isMobile = useIsMobile();
  
  // State for responsive breakpoint detection and loading
  const [currentBreakpoint, setCurrentBreakpoint] = useState('base');
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const elementRef = useRef(null);
  
  // Effect to handle responsive breakpoint detection
  useEffect(() => {
    if (!responsive || typeof window === 'undefined') return;
    
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width >= 1024) setCurrentBreakpoint('lg');
      else if (width >= 768) setCurrentBreakpoint('md');
      else if (width >= 640) setCurrentBreakpoint('sm');
      else setCurrentBreakpoint('base');
    };
    
    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, [responsive]);

  // Find icon in registry using utility function
  const iconData = iconUtils.findIcon(name);

  // Effect to handle icon loading and lazy loading
  useEffect(() => {
    if (!iconData || iconData.type !== 'png') {
      setIsLoaded(true);
      return;
    }

    const iconSrc = iconData.src;
    const loadingStatus = iconPreloader.getLoadingStatus(iconSrc);

    // Start performance monitoring
    iconPerformanceMonitor.startLoad(name, iconSrc);

    if (loadingStatus === 'loaded') {
      setIsLoaded(true);
      iconPerformanceMonitor.recordLoad(iconSrc);
      return;
    }

    // Handle lazy loading
    if (lazy && elementRef.current) {
      iconPreloader.setupLazyLoading(elementRef.current, iconSrc);
      
      // Set up a listener for when the icon loads
      const checkLoaded = () => {
        if (iconPreloader.getLoadingStatus(iconSrc) === 'loaded') {
          setIsLoaded(true);
          setLoadError(false);
          iconPerformanceMonitor.recordLoad(iconSrc);
        }
      };

      const interval = setInterval(checkLoaded, 100);
      return () => clearInterval(interval);
    } else {
      // Load immediately if not lazy
      iconPreloader.loadIcon(iconSrc)
        .then(() => {
          setIsLoaded(true);
          setLoadError(false);
          iconPerformanceMonitor.recordLoad(iconSrc);
        })
        .catch((error) => {
          setLoadError(true);
          setIsLoaded(false);
          iconPerformanceMonitor.recordError(iconSrc, error.message);
        });
    }
  }, [iconData, lazy, name]);
  
  if (!iconData) {
    // Enhanced fallback for missing icons with responsive sizing
    const fallbackDimensions = responsive 
      ? iconUtils.getResponsiveDimensions(size, currentBreakpoint !== 'base' ? currentBreakpoint : null)
      : sizeVariants[size];
    
    console.warn(`Icon "${name}" not found in registry`);
    return (
      <span 
        className={`inline-flex items-center justify-center bg-gray-200 text-gray-500 rounded text-xs font-medium ${className}`}
        style={{ 
          width: fallbackDimensions.width,
          height: fallbackDimensions.height,
          ...style 
        }}
        title={alt || `Missing icon: ${name}`}
        role="img"
        aria-label={iconUtils.generateAriaLabel({ alt: alt || `Missing icon: ${name}`, category: 'unknown' })}
        {...props}
      >
        ?
      </span>
    );
  }

  // Get responsive dimensions
  const dimensions = responsive 
    ? iconUtils.getResponsiveDimensions(size, currentBreakpoint !== 'base' ? currentBreakpoint : null)
    : sizeVariants[size];

  // Generate comprehensive aria-label for accessibility
  const ariaLabel = iconUtils.generateAriaLabel(iconData, alt);

  // Get color value using utility function
  const fillColor = iconUtils.getIconColor(iconData, color);

  // Validate color contrast for accessibility (disabled - was causing console spam)
  // To re-enable, change `&& false` to `&& true`
  if (process.env.NODE_ENV === 'development' && false && iconData.type === 'svg') {
    const contrastCheck = iconUtils.validateColorContrast(fillColor, background, size);
    if (!contrastCheck.compliant) {
      console.warn(
        `Icon "${name}" may not meet accessibility contrast requirements. ` +
        `Current ratio: ${contrastCheck.ratio}:1, Required: ${contrastCheck.requiredRatio}:1`
      );
    }
  }

  // Check for reduced motion preference
  const prefersReducedMotion = iconUtils.prefersReducedMotion();
  
  // Get animation configuration based on motion preferences
  const animationConfig = iconUtils.getAnimationConfig(animate, name);

  // Build CSS classes for responsive sizing and accessibility
  const buildIconClasses = (baseClasses = '') => {
    const classes = ['icon-base'];
    
    // Add responsive sizing class if enabled
    if (responsive) {
      classes.push(`icon-responsive-${size}`);
    }
    
    // Add emotional state class if applicable
    if (iconData.emotionalState) {
      classes.push(`icon-emotional-${iconData.emotionalState}`);
    }
    
    // Add category class
    if (iconData.category) {
      classes.push(`icon-${iconData.category}`);
    }
    
    // Add animation class for loading icons
    if (name === 'loading' && animationConfig.animate) {
      classes.push('icon-loading');
    }
    
    // Add interactive class if animate is enabled
    if (animate || iconData.animate) {
      classes.push('icon-interactive');
    }
    
    return `${classes.join(' ')} ${baseClasses} ${className}`.trim();
  };

  // PNG Icon Component with loading states and performance optimization
  if (iconData.type === 'png') {
    const iconClasses = buildIconClasses('object-contain');
    
    // Show loading placeholder for lazy-loaded icons
    if (lazy && !isLoaded && !loadError) {
      const LoadingPlaceholder = (
        <div
          ref={elementRef}
          className={`${iconClasses} bg-gray-100 animate-pulse rounded`}
          style={{
            ...(responsive ? {} : { width: dimensions.width, height: dimensions.height }),
            ...style
          }}
          role="img"
          aria-label={`Loading ${ariaLabel}`}
          {...props}
        />
      );
      return LoadingPlaceholder;
    }

    // Show error state if loading failed
    if (loadError) {
      return (
        <div
          className={`${iconClasses} bg-red-100 text-red-500 flex items-center justify-center text-xs rounded`}
          style={{
            ...(responsive ? {} : { width: dimensions.width, height: dimensions.height }),
            ...style
          }}
          role="img"
          aria-label={`Failed to load ${ariaLabel}`}
          title={`Failed to load icon: ${name}`}
          {...props}
        >
          ✕
        </div>
      );
    }
    
    const PngIcon = (
      <img
        ref={elementRef}
        src={iconData.src}
        alt={ariaLabel}
        className={iconClasses}
        style={{
          ...(responsive ? {} : { width: dimensions.width, height: dimensions.height }),
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
          ...style
        }}
        role="img"
        aria-label={ariaLabel}
        loading={lazy ? 'lazy' : 'eager'}
        onLoad={() => {
          setIsLoaded(true);
          setLoadError(false);
        }}
        onError={() => {
          setLoadError(true);
          setIsLoaded(false);
        }}
        {...props}
      />
    );

    // Apply animation only if motion is not reduced
    if (animationConfig.animate && !animationConfig.disableScale) {
      // Configure motion props based on device type
      const motionProps = isMobile ? {
        whileTap: { scale: 0.95 },
        transition: { duration: 0.1 }
      } : {
        whileHover: { scale: 1.1 },
        whileTap: { scale: 0.95 },
        transition: { duration: animationConfig.duration }
      };

      return (
        <motion.div
          {...motionProps}
          className="inline-block"
        >
          {PngIcon}
        </motion.div>
      );
    }

    return PngIcon;
  }

  // SVG Icon Component
  if (iconData.type === 'svg') {
    const iconClasses = buildIconClasses();
    
    const SvgIcon = (
      <svg
        width={responsive ? undefined : dimensions.width}
        height={responsive ? undefined : dimensions.height}
        viewBox={iconData.viewBox}
        fill={fillColor}
        className={iconClasses}
        style={{
          ...(responsive ? {} : { width: dimensions.width, height: dimensions.height }),
          ...style
        }}
        role="img"
        aria-label={ariaLabel}
        {...props}
      >
        <path d={iconData.path} />
      </svg>
    );

    // Add animation based on motion preferences and animation config
    if (animationConfig.animate && (animate || iconData.animate)) {
      const isLoadingIcon = name === 'loading';
      
      // Configure animation based on type and motion preferences
      let animateProps = {};
      let transitionProps = { duration: animationConfig.duration };
      
      if (isLoadingIcon && !animationConfig.disableRotation) {
        animateProps = { rotate: 360 };
        transitionProps = { 
          duration: animationConfig.duration || 1, 
          repeat: Infinity, 
          ease: "linear" 
        };
      } else if (!animationConfig.disableScale) {
        animateProps = { scale: [1, 1.1, 1] };
      }
      
      return (
        <motion.div
          className="inline-block"
          animate={animateProps}
          transition={transitionProps}
        >
          {SvgIcon}
        </motion.div>
      );
    }

    return SvgIcon;
  }

  return null;
};

// Convenience components for common icon categories
export const NavigationIcon = (props) => <Icon {...props} />;
export const EmotionalIcon = (props) => <Icon {...props} />;
export const FunctionalIcon = (props) => <Icon {...props} />;

// Export icon registry for external access
export { iconRegistry, iconColors, sizeVariants };

export default Icon;